import { DiffEditor, Editor } from '@monaco-editor/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getExperiment,
  getExperimentChange,
  getExperimentHistory,
} from '../../services/experiments';
import { experimentsUIUrl } from '../../variables/Pages';

function ExperimentHistoryComponent() {
  const { id } = useParams<'id'>();
  const [experimentHistory, setExperimentHistory] = React.useState<any>(null);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [expandedData, setExpandedData] = React.useState<{
    [key: string]: any;
  }>({});

  useEffect(() => {
    const fetchExperimentHistory = async () => {
      try {
        if (id) {
          const result = await getExperimentHistory(id);
          setExperimentHistory(result.changes);
        }
      } catch (error) {
        toast.error(`Error fetching experiment history: ${error}`);
      }
    };
    fetchExperimentHistory();
  }, [id]);

  const handleAccordionChange =
    (panel: string, panelDate: string) =>
    async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panelDate : false);
      if (isExpanded) {
        if (!expandedData[panelDate]) {
          const result = await getExperimentChange(panel, panelDate);
          setExpandedData((prevData) => ({
            ...prevData,
            [panelDate]: result,
          }));
        }
      }
    };

  return (
    <div>
      <Typography
        gutterBottom
        component='div'
        sx={{ color: 'text.primary' }}
        variant='h6'
      >
        Experiment Change History
      </Typography>
      {experimentHistory?.map((item: any, index: number) => {
        return (
          <Accordion
            key={index as any}
            expanded={expanded === item.date}
            onChange={handleAccordionChange(item.name, item.date)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
              >
                <Typography
                  sx={{
                    width: '220px',
                    flexShrink: 0,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  {new Date(item.date).toLocaleDateString(
                    navigator.language || 'en-US',
                    {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: navigator.language?.startsWith('en'),
                    },
                  )}
                  <br />
                  <Typography
                    component='span'
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.disabled',
                    }}
                  >
                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </Typography>
                </Typography>
                <Divider flexItem orientation='vertical' sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    color: 'text.primary',
                    flex: 1,
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  }}
                >
                  {item.message || 'N/A'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {expandedData[item.date] ? (
                <DiffEditor
                  height='90vh'
                  language='json'
                  modified={JSON.stringify(
                    expandedData[item.date].change.to,
                    null,
                    2,
                  )}
                  options={{ readOnly: true }}
                  original={JSON.stringify(
                    expandedData[item.date].change.from,
                    null,
                    2,
                  )}
                  keepCurrentOriginalModel={true}
                  keepCurrentModifiedModel={true}
                  theme='vs-dark'
                  width='100%'
                />
              ) : (
                <CircularProgress />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default function ExperimentDetail() {
  const navigate = useNavigate();
  const { id } = useParams<'id'>();
  const [experiment, setExperiment] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        if (id) {
          const result = await getExperiment(id);
          setExperiment(JSON.stringify(result, null, 2));
        }
      } catch (error) {
        toast.error(`Error fetching experiment: ${error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiment();
  }, [id]);

  if (!experiment) {
    return <div />;
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          gutterBottom
          component='div'
          sx={{ color: 'text.primary' }}
          variant='h5'
        >
          Experiment Details
        </Typography>

        <Typography
          gutterBottom
          component='div'
          sx={{ color: 'text.secondary' }}
          variant='h6'
        >
          {id}
        </Typography>
        <Button
          onClick={() => navigate(`/experiments/${id}/edit`)}
          size='small'
        >
          Edit
        </Button>
        <Typography gutterBottom component='div' sx={{ color: 'text.primary' }}>
          Current Experiment Configuration
        </Typography>
        <Button
          onClick={() => navigate(experimentsUIUrl)}
          startIcon={<ArrowBackIcon />}
          variant='outlined'
        >
          Back to Experiment List
        </Button>

        <Editor
          defaultLanguage='json'
          defaultValue={experiment}
          height='90vh'
          options={{ readOnly: true }}
          theme='vs-dark'
          value={experiment}
          width='100%'
        />
        {loading && <CircularProgress />}
        {experiment && (
          <Box mt={4} width='100%'>
            <ExperimentHistoryComponent />
          </Box>
        )}
      </Box>
    </Box>
  );
}
